/**
 * WeaveIn.ai Platform - TCO Service Referrals API Service
 * TypeScript service layer for Trust Care Organization (TCO) service referral management
 *
 * This service provides API methods for:
 * - Creating and managing TCO service referrals
 * - Provider matching and selection
 * - Workflow management and automation
 * - Dashboard statistics
 */

import { apiClient } from '../apiClient';
import { initializeActiveCboId } from '../../utils/auth';

// ============================================================================
// TYPE DEFINITIONS - TCO SOURCE TYPES
// ============================================================================

export type TCOSourceType =
  | 'care_plan_goal'       // From care plan goal/intervention
  | 'sdoh_screening'       // From SDOH screening result
  | 'navigator_referral'   // Navigator/CHW initiated
  | 'member_request'       // Member self-initiated via portal
  | 'pcp_referral'         // Primary care provider referral
  | 'discharge_planning'   // Hospital/facility discharge
  | 'case_review'          // From case review/huddle
  | 'ai_recommended'       // AI-driven recommendation
  | 'ecm_referral'         // Enhanced Care Management referral
  | 'cbo_assessment'       // From CBO assessment
  | 'community_partner'    // Community partner referral
  | 'crisis_intervention'; // Crisis/emergency intervention

export type UrgencyLevel = 'routine' | 'urgent' | 'emergent';

export type ReferralStatus =
  | 'created'
  | 'pending_consent'
  | 'matching'
  | 'matched'
  | 'provider_notified'
  | 'provider_accepted'
  | 'provider_declined'
  | 'service_scheduled'
  | 'service_started'
  | 'service_completed'
  | 'billing_ready'
  | 'billed'
  | 'paid'
  | 'closed'
  | 'cancelled'
  | 'expired'
  | 'failed';

export type SDOHDomainCode =
  | 'HOUSING'
  | 'FOOD_NUTRITION'
  | 'TRANSPORTATION'
  | 'ECONOMIC_STABILITY'
  | 'EDUCATION_EMPLOYMENT'
  | 'SOCIAL_SUPPORT'
  | 'HEALTH_BEHAVIORS'
  | 'SAFETY';

export type ServiceOutcome =
  | 'successful'
  | 'partial'
  | 'unsuccessful'
  | 'no_show'
  | 'cancelled_by_member'
  | 'cancelled_by_provider';

// ============================================================================
// TYPE DEFINITIONS - TCO SERVICE REFERRALS
// ============================================================================

export interface TCOServiceReferral {
  id: number;
  referral_number: string;
  status: ReferralStatus;
  source_type: TCOSourceType;
  referral_context: 'care_organization';

  // Member context
  member_id: number;
  member_name?: string;
  enrollment_id?: number;
  care_plan_id?: number;
  care_plan_goal_id?: number;

  // Organization
  cbo_id: number;
  cbo_name?: string;
  referring_navigator_id?: number;
  referring_navigator_name?: string;

  // Service
  service_type_id?: number;
  service_type_code: string;
  service_type_name?: string;
  category_name?: string;
  domain_name?: string;
  urgency_level: UrgencyLevel;
  service_description?: string;
  cbo_only?: boolean;

  // SDOH
  sdoh_domain_code?: SDOHDomainCode;
  sdoh_category_code?: string;
  sdoh_assessment_id?: number;
  sdoh_risk_score?: number;
  sdoh_needs?: string[];

  // Matching
  auto_matched: boolean;
  matched_at?: string;
  match_count: number;
  selected_provider_id?: number;
  selected_provider_type?: string;
  selected_provider_name?: string;
  selected_match_score?: number;

  // Acceptance
  acceptance_status: string;
  acceptance_expires_at?: string;
  provider_accepted_at?: string;

  // Scheduling
  scheduled_date?: string;
  scheduled_time?: string;
  appointment_confirmed: boolean;

  // Service delivery
  service_started_at?: string;
  service_completed_at?: string;
  service_outcome?: ServiceOutcome;
  member_satisfaction_score?: number;

  // Billing
  claim_id?: number;
  billable_amount?: number;

  // SLA
  sla_acceptance_breached: boolean;
  sla_service_start_breached: boolean;

  // Clinical<->social handoff provenance (why a clinical signal spawned this referral)
  origin_clinical_trigger?: 'diagnosis' | 'care_gap' | 'discharge' | 'screen';
  origin_icd10?: string;
  // Clinician loop-back (outcome pushed back to the referring provider)
  pcp_notification_sent?: boolean;
  pcp_notified_at?: string;

  // Audit
  created_at: string;
  updated_at?: string;
}

export interface TCOServiceReferralCreate {
  source_type: TCOSourceType;
  care_plan_id?: number;
  care_plan_goal_id?: number;
  member_id: number;
  enrollment_id?: number;
  service_type_code: string;
  urgency_level?: UrgencyLevel;
  service_description?: string;
  clinical_notes?: string;
  sdoh_domain_code?: SDOHDomainCode;
  sdoh_category_code?: string;
  sdoh_assessment_id?: number;
  sdoh_risk_score?: number;
  sdoh_needs?: string[];
  auto_match?: boolean;
  max_providers?: number;
  prefer_network_providers?: boolean;
}

export interface TCOServiceReferralUpdate {
  urgency_level?: UrgencyLevel;
  service_description?: string;
  clinical_notes?: string;
  sdoh_domain_code?: SDOHDomainCode;
  sdoh_category_code?: string;
  sdoh_risk_score?: number;
  sdoh_needs?: string[];
}

export interface ReferralMatch {
  id: number;
  provider_type: string;
  provider_id: string;
  provider_name?: string;
  match_score?: number;
  distance_miles?: number;
  match_rank?: number;
  is_selected: boolean;
  response_status: 'pending' | 'accepted' | 'declined' | 'expired';
  notified_at?: string;
  response_at?: string;
  provider_phone?: string;
  provider_email?: string;
  provider_address?: string;
}

export interface ScheduleServiceRequest {
  scheduled_date: string;
  scheduled_time?: string;
  scheduled_duration_minutes?: number;
  scheduling_notes?: string;
}

export interface CompleteServiceRequest {
  service_outcome: ServiceOutcome;
  outcome_notes?: string;
  actual_service_date?: string;
  actual_duration_minutes?: number;
  actual_units_delivered?: number;
  member_satisfaction_score?: number;
  member_feedback?: string;
}

export interface StatusTransitionRequest {
  new_status: string;
  notes?: string;
}

export interface StateHistoryEntry {
  id: number;
  from_status?: string;
  to_status: string;
  trigger_type?: string;
  changed_by_user_id?: number;
  changed_by_name?: string;
  notes?: string;
  changed_at: string;
}

export interface AutoMatchResult {
  success: boolean;
  matched_count?: number;
  matches?: Array<{
    provider_type: string;
    provider_id: string;
    provider_name?: string;
    match_score?: number;
    distance_miles?: number;
    rank?: number;
  }>;
  error?: string;
}

// ============================================================================
// TYPE DEFINITIONS - TCO WORKFLOWS
// ============================================================================

export type TriggerType =
  | 'manual'
  | 'sdoh_threshold'
  | 'care_gap_detected'
  | 'enrollment_milestone'
  | 'ai_recommended'
  | 'scheduled_review'
  | 'assessment_completed'
  | 'clinical_signal_detected';

export type WorkflowStatus =
  | 'pending'
  | 'queued'
  | 'in_progress'
  | 'waiting_response'
  | 'paused'
  | 'completed'
  | 'cancelled'
  | 'failed';

export type WorkflowOutcome =
  | 'success'
  | 'member_engaged'
  | 'referral_created'
  | 'member_declined'
  | 'no_response'
  | 'escalated'
  | 'failed'
  | 'cancelled_by_user';

export interface WorkflowTemplate {
  id: number;
  cbo_id: number;
  template_name: string;
  template_type: string;
  description?: string;
  trigger_type: TriggerType;
  trigger_config: Record<string, any>;
  auto_trigger_enabled: boolean;
  trigger_sdoh_domains?: string[];
  trigger_min_risk_score: number;
  workflow_steps: Array<{
    name: string;
    step_type: string;
    order: number;
    config?: Record<string, any>;
  }>;
  default_channel: string;
  use_ml_timing: boolean;
  max_outreach_attempts: number;
  response_timeout_hours: number;
  auto_create_referral: boolean;
  referral_service_types?: string[];
  max_providers_to_match: number;
  ai_model_config?: Record<string, any>;
  is_active: boolean;
  target_entity_type?: 'member' | 'contact' | 'both';
  created_at: string;
  updated_at?: string;
}

export interface WorkflowTemplateCreate {
  template_name: string;
  template_type?: string;
  description?: string;
  trigger_type?: TriggerType;
  trigger_config?: Record<string, any>;
  auto_trigger_enabled?: boolean;
  trigger_sdoh_domains?: string[];
  trigger_min_risk_score?: number;
  trigger_enrollment_events?: string[];
  workflow_steps?: Array<{
    name: string;
    step_type: string;
    order: number;
    config?: Record<string, any>;
  }>;
  default_channel?: string;
  use_ml_timing?: boolean;
  max_outreach_attempts?: number;
  response_timeout_hours?: number;
  auto_create_referral?: boolean;
  referral_service_types?: string[];
  referral_urgency_default?: string;
  max_providers_to_match?: number;
  prefer_network_providers?: boolean;
  cbo_only_for_sdoh?: boolean;
  ai_model_config?: {
    primary_model?: string;
    decision_model?: string;
    communication_model?: string;
    personalization_model?: string;
    model_parameters?: Record<string, any>;
  };
  is_active?: boolean;
  target_entity_type?: 'member' | 'contact' | 'both';
}

export interface WorkflowInstance {
  id: number;
  template_id?: number;
  template_name?: string;
  cbo_id: number;
  member_id: number;
  member_name?: string;
  enrollment_id?: number;
  trigger_type: TriggerType;
  trigger_source?: Record<string, any>;
  status: WorkflowStatus;
  current_step: number;
  current_step_name?: string;
  total_steps: number;
  step_executions?: StepExecution[];
  outreach_attempts: number;
  last_outreach_at?: string;
  member_response?: string;
  service_referral_id?: number;
  referral_number?: string;
  outcome?: WorkflowOutcome;
  assigned_to?: number;
  assigned_to_name?: string;
  priority: number;
  created_at: string;
  updated_at?: string;
}

export type StepExecutionStatus = 'pending' | 'executing' | 'waiting' | 'completed' | 'failed' | 'skipped';

export interface StepExecution {
  id: number;
  instance_id: number;
  step_number: number;
  step_name?: string;
  step_type: string;
  status: StepExecutionStatus;
  scheduled_at?: string;
  started_at?: string;
  completed_at?: string;
  duration_seconds?: number;
  step_input?: Record<string, any>;
  step_output?: Record<string, any>;
  error_message?: string;
  outreach_channel?: string;
  delivery_status?: string;
  decision_made?: string;
  decision_reason?: string;
  decision_confidence?: number;
  attempt_number: number;
  max_attempts: number;
  // Form integration
  form_template_id?: number;
  form_instance_id?: number;
  form_completed?: boolean;
  form_status?: string; // draft, in_progress, submitted, completed, approved
  // Incentive tracking
  incentive_awarded?: boolean;
  incentive_amount?: number;
  created_at?: string;
}

export interface StepActionRequest {
  action: 'start' | 'complete' | 'skip' | 'fail';
  notes?: string;
  outreach_channel?: string;
  decision_made?: string;
  decision_reason?: string;
  result_data?: Record<string, any>;
}

export interface WorkflowInitiateRequest {
  member_ids: number[];
  template_id: number;
  trigger_type?: TriggerType;
  trigger_source?: Record<string, any>;
  priority?: number;
}

export interface WorkflowInitiateResponse {
  success: boolean;
  workflows_created: number;
  workflow_ids: number[];
  errors: Array<{ member_id: number; error: string }>;
}

export interface DashboardStats {
  total_pending: number;
  total_in_progress: number;
  total_waiting_response: number;
  total_completed_30d: number;
  total_referrals_created_30d: number;
  avg_completion_hours?: number;
  success_rate?: number;
  workflows_by_trigger: Record<string, number>;
  workflows_by_outcome: Record<string, number>;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export function getReferralStatusLabel(status: ReferralStatus): string {
  const labels: Record<ReferralStatus, string> = {
    created: 'Created',
    pending_consent: 'Pending Consent',
    matching: 'Finding Providers',
    matched: 'Providers Matched',
    provider_notified: 'Provider Notified',
    provider_accepted: 'Provider Accepted',
    provider_declined: 'Provider Declined',
    service_scheduled: 'Scheduled',
    service_started: 'In Progress',
    service_completed: 'Completed',
    billing_ready: 'Ready for Billing',
    billed: 'Billed',
    paid: 'Paid',
    closed: 'Closed',
    cancelled: 'Cancelled',
    expired: 'Expired',
    failed: 'Failed',
  };
  return labels[status] || status;
}

export function getReferralStatusColor(status: ReferralStatus): string {
  const colors: Record<ReferralStatus, string> = {
    created: 'bg-gray-100 text-gray-800',
    pending_consent: 'bg-yellow-100 text-yellow-800',
    matching: 'bg-blue-100 text-blue-800',
    matched: 'bg-blue-100 text-blue-800',
    provider_notified: 'bg-purple-100 text-purple-800',
    provider_accepted: 'bg-green-100 text-green-800',
    provider_declined: 'bg-red-100 text-red-800',
    service_scheduled: 'bg-indigo-100 text-indigo-800',
    service_started: 'bg-cyan-100 text-cyan-800',
    service_completed: 'bg-green-100 text-green-800',
    billing_ready: 'bg-amber-100 text-amber-800',
    billed: 'bg-orange-100 text-orange-800',
    paid: 'bg-emerald-100 text-emerald-800',
    closed: 'bg-gray-100 text-gray-800',
    cancelled: 'bg-red-100 text-red-800',
    expired: 'bg-gray-100 text-gray-800',
    failed: 'bg-red-100 text-red-800',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
}

export function getUrgencyLabel(urgency: UrgencyLevel): string {
  const labels: Record<UrgencyLevel, string> = {
    routine: 'Routine',
    urgent: 'Urgent',
    emergent: 'Emergent',
  };
  return labels[urgency] || urgency;
}

export function getUrgencyColor(urgency: UrgencyLevel): string {
  const colors: Record<UrgencyLevel, string> = {
    routine: 'bg-green-100 text-green-800',
    urgent: 'bg-yellow-100 text-yellow-800',
    emergent: 'bg-red-100 text-red-800',
  };
  return colors[urgency] || 'bg-gray-100 text-gray-800';
}

export function getSourceTypeLabel(sourceType: TCOSourceType): string {
  const labels: Record<TCOSourceType, string> = {
    care_plan_goal: 'Care Plan Goal',
    sdoh_screening: 'SDOH Screening',
    navigator_referral: 'Navigator Referral',
    member_request: 'Member Request',
    pcp_referral: 'PCP Referral',
    discharge_planning: 'Discharge Planning',
    case_review: 'Case Review',
    ai_recommended: 'AI Recommended',
    ecm_referral: 'ECM Referral',
    cbo_assessment: 'CBO Assessment',
    community_partner: 'Community Partner',
    crisis_intervention: 'Crisis Intervention',
  };
  return labels[sourceType] || sourceType;
}

// ============================================================================
// API SERVICE - TCO SERVICE REFERRALS
// ============================================================================

export const tcoServiceReferralsAPI = {
  // -------------------------------------------------------------------------
  // Referral CRUD
  // -------------------------------------------------------------------------

  /**
   * Create a new TCO service referral
   */
  async create(data: TCOServiceReferralCreate): Promise<TCOServiceReferral> {
    await initializeActiveCboId();
    const response = await apiClient.post('/api/tco/service-referrals', data);
    return response.data;
  },

  /**
   * List TCO service referrals with optional filters
   */
  async list(params: {
    status?: string;
    member_id?: number;
    care_plan_id?: number;
    service_type_code?: string;
    urgency_level?: string;
    source_type?: string;
    include_closed?: boolean;
    limit?: number;
    offset?: number;
  } = {}): Promise<TCOServiceReferral[]> {
    await initializeActiveCboId();
    const response = await apiClient.get('/api/tco/service-referrals', { params });
    return response.data;
  },

  /**
   * Get a specific TCO service referral by ID
   */
  async get(referralId: number): Promise<TCOServiceReferral> {
    await initializeActiveCboId();
    const response = await apiClient.get(`/api/tco/service-referrals/${referralId}`);
    return response.data;
  },

  /**
   * Update a TCO service referral
   */
  async update(referralId: number, data: TCOServiceReferralUpdate): Promise<TCOServiceReferral> {
    await initializeActiveCboId();
    const response = await apiClient.put(`/api/tco/service-referrals/${referralId}`, data);
    return response.data;
  },

  // -------------------------------------------------------------------------
  // Provider Matching
  // -------------------------------------------------------------------------

  /**
   * Trigger auto-matching for a referral
   */
  async autoMatch(referralId: number, params: {
    max_providers?: number;
    prefer_network?: boolean;
  } = {}): Promise<AutoMatchResult> {
    await initializeActiveCboId();
    const response = await apiClient.post(
      `/api/tco/service-referrals/${referralId}/auto-match`,
      null,
      { params }
    );
    return response.data;
  },

  /**
   * Get matched providers for a referral
   */
  async getMatches(referralId: number): Promise<ReferralMatch[]> {
    await initializeActiveCboId();
    const response = await apiClient.get(`/api/tco/service-referrals/${referralId}/matches`);
    return response.data;
  },

  /**
   * Select a provider for a referral
   */
  async selectProvider(
    referralId: number,
    providerType: string,
    providerId: string
  ): Promise<{ success: boolean; provider_name: string; status: string }> {
    await initializeActiveCboId();
    const response = await apiClient.post(
      `/api/tco/service-referrals/${referralId}/select-provider`,
      null,
      { params: { provider_type: providerType, provider_id: providerId } }
    );
    return response.data;
  },

  // -------------------------------------------------------------------------
  // Service Lifecycle
  // -------------------------------------------------------------------------

  /**
   * Schedule a service appointment
   */
  async scheduleService(
    referralId: number,
    data: ScheduleServiceRequest
  ): Promise<{ success: boolean; status: string; scheduled_date: string }> {
    await initializeActiveCboId();
    const response = await apiClient.post(
      `/api/tco/service-referrals/${referralId}/schedule`,
      data
    );
    return response.data;
  },

  /**
   * Mark service as completed
   */
  async completeService(
    referralId: number,
    data: CompleteServiceRequest
  ): Promise<{ success: boolean; status: string; outcome: string }> {
    await initializeActiveCboId();
    const response = await apiClient.post(
      `/api/tco/service-referrals/${referralId}/complete`,
      data
    );
    return response.data;
  },

  /**
   * Transition referral status
   */
  async transitionStatus(
    referralId: number,
    data: StatusTransitionRequest
  ): Promise<{ success: boolean; from_status: string; to_status: string }> {
    await initializeActiveCboId();
    const response = await apiClient.post(
      `/api/tco/service-referrals/${referralId}/transition`,
      data
    );
    return response.data;
  },

  /**
   * Get referral state history
   */
  async getHistory(referralId: number): Promise<StateHistoryEntry[]> {
    await initializeActiveCboId();
    const response = await apiClient.get(`/api/tco/service-referrals/${referralId}/history`);
    return response.data;
  },
};

// ============================================================================
// API SERVICE - TCO WORKFLOWS
// ============================================================================

export const tcoWorkflowsAPI = {
  // -------------------------------------------------------------------------
  // Workflow Templates
  // -------------------------------------------------------------------------

  /**
   * Create a new workflow template
   */
  async createTemplate(data: WorkflowTemplateCreate): Promise<WorkflowTemplate> {
    console.log('[TCO Workflows API] createTemplate called with:', data);
    await initializeActiveCboId();
    console.log('[TCO Workflows API] CBO ID initialized, sending POST request');
    const response = await apiClient.post('/api/tco/workflows/templates', data);
    console.log('[TCO Workflows API] createTemplate response:', response.data);
    return response.data;
  },

  /**
   * List workflow templates
   */
  async listTemplates(params: {
    is_active?: boolean;
    trigger_type?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<WorkflowTemplate[]> {
    await initializeActiveCboId();
    const response = await apiClient.get('/api/tco/workflows/templates', { params });
    return response.data;
  },

  /**
   * Get a specific workflow template
   */
  async getTemplate(templateId: number): Promise<WorkflowTemplate> {
    await initializeActiveCboId();
    const response = await apiClient.get(`/api/tco/workflows/templates/${templateId}`);
    return response.data;
  },

  /**
   * Update a workflow template
   */
  async updateTemplate(
    templateId: number,
    data: Partial<WorkflowTemplateCreate>
  ): Promise<WorkflowTemplate> {
    await initializeActiveCboId();
    const response = await apiClient.put(`/api/tco/workflows/templates/${templateId}`, data);
    return response.data;
  },

  /**
   * Delete (deactivate) a workflow template
   */
  async deleteTemplate(templateId: number): Promise<{ success: boolean; message: string }> {
    await initializeActiveCboId();
    const response = await apiClient.delete(`/api/tco/workflows/templates/${templateId}`);
    return response.data;
  },

  // -------------------------------------------------------------------------
  // Workflow Instances
  // -------------------------------------------------------------------------

  /**
   * Initiate workflows for members
   */
  async initiate(data: WorkflowInitiateRequest): Promise<WorkflowInitiateResponse> {
    await initializeActiveCboId();
    const response = await apiClient.post('/api/tco/workflows/initiate', data);
    return response.data;
  },

  /**
   * List workflow instances
   */
  async listInstances(params: {
    status?: string;
    member_id?: number;
    template_id?: number;
    assigned_to?: number;
    include_completed?: boolean;
    limit?: number;
    offset?: number;
  } = {}): Promise<WorkflowInstance[]> {
    await initializeActiveCboId();
    const response = await apiClient.get('/api/tco/workflows/instances', { params });
    return response.data;
  },

  /**
   * Get a specific workflow instance
   */
  async getInstance(instanceId: number): Promise<WorkflowInstance> {
    await initializeActiveCboId();
    const response = await apiClient.get(`/api/tco/workflows/instances/${instanceId}`);
    return response.data;
  },

  /**
   * Record member response for a workflow
   */
  async recordResponse(
    instanceId: number,
    data: {
      response: string;
      response_content?: string;
      response_sentiment?: string;
    }
  ): Promise<{ success: boolean; response_recorded: string }> {
    await initializeActiveCboId();
    const response = await apiClient.post(
      `/api/tco/workflows/instances/${instanceId}/response`,
      data
    );
    return response.data;
  },

  /**
   * Create referral from workflow instance
   */
  async createReferralFromWorkflow(
    instanceId: number,
    data: {
      service_types: string[];
      urgency?: string;
      notes?: string;
    }
  ): Promise<{ success: boolean; referral_id: number; referral_number: string }> {
    await initializeActiveCboId();
    const response = await apiClient.post(
      `/api/tco/workflows/instances/${instanceId}/create-referral`,
      data
    );
    return response.data;
  },

  /**
   * Start a pending workflow — creates step execution records
   */
  async startWorkflow(
    instanceId: number
  ): Promise<{ success: boolean; status: string; total_steps: number; steps_created: number }> {
    await initializeActiveCboId();
    const response = await apiClient.post(
      `/api/tco/workflows/instances/${instanceId}/start`
    );
    return response.data;
  },

  /**
   * List step executions for a workflow instance
   */
  async listStepExecutions(instanceId: number): Promise<StepExecution[]> {
    await initializeActiveCboId();
    const response = await apiClient.get(
      `/api/tco/workflows/instances/${instanceId}/steps`
    );
    return response.data;
  },

  /**
   * Execute an action on a workflow step
   */
  async executeStep(
    instanceId: number,
    stepNumber: number,
    data: StepActionRequest
  ): Promise<{ success: boolean; action: string; step_number: number; step_name: string }> {
    await initializeActiveCboId();
    const response = await apiClient.post(
      `/api/tco/workflows/instances/${instanceId}/steps/${stepNumber}/execute`,
      data
    );
    return response.data;
  },

  /**
   * Cancel a workflow instance
   */
  async cancelInstance(
    instanceId: number,
    reason?: string
  ): Promise<{ success: boolean; status: string }> {
    await initializeActiveCboId();
    const response = await apiClient.post(
      `/api/tco/workflows/instances/${instanceId}/cancel`,
      null,
      { params: { reason } }
    );
    return response.data;
  },

  // -------------------------------------------------------------------------
  // Dashboard
  // -------------------------------------------------------------------------

  /**
   * Get workflow dashboard statistics
   */
  async getDashboard(): Promise<DashboardStats> {
    await initializeActiveCboId();
    const response = await apiClient.get('/api/tco/workflows/dashboard');
    return response.data;
  },

  // -------------------------------------------------------------------------
  // Connected-agent model: step/trigger catalog, human-approval gates,
  // gated queue, and the instance journey (mirrors the Agents→Tickets model).
  // -------------------------------------------------------------------------

  /** Catalog of connected step types + trigger types for the builder. */
  async getStepCatalog(): Promise<{
    step_types: Array<{ key: string; label: string; group: string; description: string; config: string[] }>;
    trigger_types: Array<{ key: string; label: string; description?: string; config?: string[] }>;
    agents?: Array<{ key: string; label: string; source: string; model?: string; gate_policy?: string; status?: string }>;
    channels?: Array<{ key: string; label: string }>;
  }> {
    await initializeActiveCboId();
    const response = await apiClient.get('/api/tco/workflows/step-catalog');
    return response.data;
  },

  /** Steps awaiting human approval across the org (the care-workflow review queue). */
  async getGated(): Promise<{
    gated: Array<{
      instance_id: number;
      step_number: number;
      step_name: string;
      step_type: string;
      member_id: number;
      template_id: number;
      template_name: string | null;
      trigger_type: string;
      trigger_source: Record<string, unknown> | null;
      started_at: string | null;
    }>;
    count: number;
  }> {
    await initializeActiveCboId();
    const response = await apiClient.get('/api/tco/workflows/gated');
    return response.data;
  },

  /** Full resolution arc of an instance: steps (with telemetry) + event stream. */
  async getJourney(instanceId: number): Promise<{
    instance: Record<string, unknown>;
    steps: Array<Record<string, unknown>>;
    events: Array<{ event_type: string; event_message: string; event_data: Record<string, unknown>; created_at: string }>;
  }> {
    await initializeActiveCboId();
    const response = await apiClient.get(`/api/tco/workflows/instances/${instanceId}/journey`);
    return response.data;
  },

  /** Approve a parked human-gate step → the workflow resumes. */
  async approveStep(instanceId: number, stepNumber: number, notes = ''): Promise<Record<string, unknown>> {
    await initializeActiveCboId();
    const response = await apiClient.post(
      `/api/tco/workflows/instances/${instanceId}/steps/${stepNumber}/approve`,
      { notes }
    );
    return response.data;
  },

  /** Reject a parked human-gate step → the workflow halts (declined at gate). */
  async rejectStep(instanceId: number, stepNumber: number, notes = ''): Promise<Record<string, unknown>> {
    await initializeActiveCboId();
    const response = await apiClient.post(
      `/api/tco/workflows/instances/${instanceId}/steps/${stepNumber}/reject`,
      { notes }
    );
    return response.data;
  },
};

export default {
  referrals: tcoServiceReferralsAPI,
  workflows: tcoWorkflowsAPI,
};
